/**
 * AppRouter — top-level route table for the patient portal.
 *
 * v0.8.6: dual-mode routing across two flags
 * (`PIN_LOGIN_ENABLED`, `MAGIC_LINK_ENABLED`). The four combinations:
 *
 * **PIN=true / ML=false** — PIN+DOB only (today's default; pre-v0.8.0
 * behavior):
 *   /login                   → LoginPage (PIN + DOB)
 *   *                        → behind <RequireAuth>
 *     /                      → redirect to /pay
 *     /pay                   → balance-pay flow
 *     /checkin/:appointmentId → precollect flow
 *     /success               → SuccessPage
 *   anything else            → redirect to /
 *
 * **PIN=true / ML=true** — Dual-mode (v0.8.6+ rollout state):
 *   /login                   → LoginPage with "Have a login code?" link
 *   /login/code              → ManualLoginPage (manual magic-link entry)
 *   *                        → behind <RequireAuth> (same as above)
 *   /:token                  → MagicLinkLandingPage (single-segment catch-all)
 *   anything multi-segment   → redirect to /login
 *
 * **PIN=false / ML=true** — Magic-link only (Phase C; post-cutover):
 *   /login                   → ManualLoginPage (manual magic-link entry)
 *   *                        → behind <RequireAuth>
 *   /:token                  → MagicLinkLandingPage
 *   anything multi-segment   → redirect to /login
 *
 * **PIN=false / ML=false** — Safety fallback:
 *   At least one entry path must work. Defaults to PIN+DOB at /login.
 *   In practice this combo shouldn't ship; the per-flag defaults
 *   prevent it.
 *
 * The single-segment `:token` route is only mounted when
 * `MAGIC_LINK_ENABLED`. Per ledger #40, it's placed AFTER all
 * explicit routes so `/login`, `/login/code`, `/pay`, etc. continue
 * to win.
 *
 * All paths above are relative to the router's `basename`, which comes from
 * Vite's `base` (see `lib/basePath.ts`). On the shared dev host the portal
 * is mounted at `/patient-portal/`, so `/login` is really
 * `/patient-portal/login` in the address bar.
 *
 * Per decision ledger #43 (revised #53), #44, #53.
 */
import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
  type RouteObject,
} from 'react-router-dom';

import { RequireAuth } from './auth/RequireAuth';
import { ROUTER_BASENAME } from './lib/basePath';
import { LoginPage } from './pages/login/LoginPage';
import { ManualLoginPage } from './pages/login/ManualLoginPage';
import { MagicLinkLandingPage } from './pages/login/MagicLinkLandingPage';
import { PayPage } from './pages/pay/PayPage';
import { SuccessPage } from './pages/success/SuccessPage';
import {
  MAGIC_LINK_ENABLED,
  PIN_LOGIN_ENABLED,
} from './lib/featureFlags';

// Routes that exist regardless of auth flags. The flags only change
// the entry-path routes (login pages + optional token catch-all).
const protectedRoutes: RouteObject = {
  element: <RequireAuth />,
  children: [
    { index: true, element: <Navigate to="/pay" replace /> },
    { path: 'pay', element: <PayPage /> },
    { path: 'checkin/:appointmentId', element: <PayPage /> },
    { path: 'success', element: <SuccessPage /> },
  ],
};

// The entry-path routes vary by flag combination. Computed once at
// module load (flags are static for the page lifetime).
function buildEntryRoutes(): RouteObject[] {
  // Phase: magic-link only (PIN off, ML on)
  if (MAGIC_LINK_ENABLED && !PIN_LOGIN_ENABLED) {
    return [
      { path: '/login', element: <ManualLoginPage /> },
      protectedRoutes,
      // Single-segment token catch-all. After explicit routes so they win.
      { path: ':token', element: <MagicLinkLandingPage /> },
      { path: '*', element: <Navigate to="/login" replace /> },
    ];
  }

  // Phase: dual-mode (PIN on, ML on)
  if (MAGIC_LINK_ENABLED && PIN_LOGIN_ENABLED) {
    return [
      { path: '/login', element: <LoginPage /> },
      // Manual magic-link entry is reachable via "Have a login code?"
      // link on the PIN+DOB page. Placed at /login/code so the route
      // hierarchy makes the relationship visible (subroute of /login)
      // without nesting them as React Router children (they don't
      // share layout).
      { path: '/login/code', element: <ManualLoginPage /> },
      protectedRoutes,
      { path: ':token', element: <MagicLinkLandingPage /> },
      { path: '*', element: <Navigate to="/login" replace /> },
    ];
  }

  // Phase: PIN+DOB only (PIN on, ML off — today's default)
  // ALSO the safety fallback when both are false (unlikely but safe).
  return [
    { path: '/login', element: <LoginPage /> },
    protectedRoutes,
    { path: '*', element: <Navigate to="/" replace /> },
  ];
}

export const routeTable: RouteObject[] = buildEntryRoutes();

// `basename` is required whenever the portal is served under a URL
// sub-path (`/patient-portal/` on the shared dev host). Without it the
// router matched the mount prefix itself against the `:token` catch-all
// below, so visiting `/patient-portal/` landed on the magic-link page with
// "patient-portal" as the token instead of on the login page.
const router = createBrowserRouter(routeTable, { basename: ROUTER_BASENAME });

export function AppRouter() {
  return <RouterProvider router={router} />;
}
